import React, { useState, useEffect, useRef } from "react";

import { Trans, useTranslation } from "react-i18next";

import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import { getCurrentDateYMD } from "../../utils/DateHelper";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import { getRoles } from "../../utils/OktaHelpers";
import { ROLES } from "../../utils/constants";
import { isAuthorized } from "../../utils/RolesHelpers";
import { getSessionFilters, getMappedFilters } from "../../utils/FilterHelpers";
import LogCapture from "../../utils/LogManagement/LogHelper";
import ProductTags from "../../components/Tags/ProductTags";
import { longDateTime } from "../../utils/DateHelper";
import OrganizationSubscriptionsAPI from "../../API/organizationSubscriptions.api";
import BaseAlertModal from "../../components/Modals/BaseAlertModal";
import BaseModal from "../../components/Modals/BaseModal";
import { durationFormatter } from "../../utils/TagsFormatters";
import { renderBulletPoints } from "../../utils/UIHelpers";
import ChangeCard from "../../components/Cards/ChangeCard";
import ProductVersionAPI from "../../API/productVersion.api";
import SubscribedBiasCorrection from "../../components/Modals/SubscribedBiasCorrection";
import ActionsDropdown from "../../components/Dropdown/ActionsDropdown";
import ProductAccessModal from "../../components/Modals/ProductAccessModal";
import { setObject, getObject } from "../../utils/StorageHelpers";
import DisplayUnitModal from "../../components/Modals/DisplayUnitModal";
import LookupsAPI from "../../API/lookups.api";
import { isEmpty } from "lodash";
import useTranslationCallback from "../../utils/CustomHooks/useTranslationCallback";
import NestedTable from "../../components/Tables/NestedTable";

const SubscribedProduct = () => {
    const [isSubscriptionsLoading, setIsSubscriptionsLoading] = useState(true);

    const [forceDeployModal, setForceDeployModal] = useState(false);
    const [unsubscribeModal, setUnsubscribeModal] = useState(false);

    const [conflictedMaterials, setConflictedMaterials] = useState([]);

    //Subscriptions states
    const [subscriptions, setSubscriptions] = useState([]);
    const [subscriptionsBiasModal, setSubscriptionsBiasModal] = useState(false);
    const [displayUnitModal, setDisplayUnitModal] = useState(false);
    const [subscriptionTotalCount, setSubscriptionTotalCount] = useState(0);
    const [subscriptionCurrentPage, setSubscriptionCurrentPage] = useState(1);
    const [subscriptionFiltersUsed, setSubscriptionFiltersUsed] =
        useState(false);
    const [subscriptionFilters, setSubscriptionFilters] = useState({
        productName: "",
        materialName: "",
        subscribedAt: {},
    });
    const [subscriptionAppliedFilters, setSubscriptionAppliedFilters] =
        useState(() => getSessionFilters("subscription-model-filters"));
    const [subscriptionSortBy, setSubscriptionSortBy] = useState(undefined);
    const [subscriptionSortDirection, setSubscriptionSortDirection] =
        useState(undefined);
    const [subscriptionMap, setSubscriptionMap] = useState({});
    const [selectedProductDetails, setSelectedProductDetails] = useState({});

    const [upgradeModal, setUpgradeModal] = useState(false);
    const [upgradeModalSubmitting, setUpgradeModalSubmitting] = useState(false);
    const [accessModal, setAccessModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedLatestVersionData, setSelectedLatestVersionData] =
        useState(null);

    const [isCheckboxSelected, setIsCheckboxSelected] = useState(false);
    const [isModalLoading, setIsModalLoading] = useState(false);
    const [isModalSubmitting, setIsModalSubmitting] = useState(false);
    const { t } = useTranslation();

    const actionRow = useRef({});

    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Subscribed-Products-itemsPerPage", true) || 10
    );

    const subscribedHeaders = [
        { label: t("name"), name: "productName" },
        { label: t("materialName"), name: "productMaterialName" },
        { label: t("parameters"), name: "parameters", skipSort: true },
        { label: t("type"), name: "isBundle", skipSort: true },
        { label: t("developedBy"), name: "developedBy" },
        { label: t("subscriptionPlan"), name: "duration", skipSort: true },
        { label: t("subscribedAt"), name: "subscribedAt" },
        { label: t("status"), name: "deployed" },
        { label: t("latest"), name: "isLatest", skipSort: true },
        {
            label: t("actions"),
            name: "actions",
            skipSort: true,
            roles: !isAuthorized(
                [ROLES.support, ROLES.partnerAdmin, ROLES.admin],
                getRoles()
            ),
        },
    ];

    const getMaterialNames = (row) => {
        if (row?.material) return [row?.material?.materialName];

        return row.bundleProducts.map(
            (bundleProduct) => bundleProduct?.material?.materialName
        );
    };

    const getSubParameterNames = (row) => {
        let parameters = 0;

        row.bundleProducts.forEach((elem) => {
            parameters += elem.parameters.length;
        });

        return [`${parameters} ${t("parameters")}`];
    };

    const getSubDeployedStatus = (row) => {
        const deployed = row.bundleProducts.filter(
            (elem) => elem?.material?.deployed
        );

        if (isEmpty(deployed)) {
            return (
                <div className="tag-ele tag-undeployed px-2">
                    {t("notDeployed")}
                </div>
            );
        }

        return (
            <div className="tag-ele tag-deployed px-2">
                {deployed.length === row.bundleProducts?.length
                    ? t("deployed")
                    : `${deployed.length} ${t("deployedOutOf")} ${
                          row.bundleProducts?.length
                      }`}
            </div>
        );
    };

    const subscribedKeys = [
        {
            name: "name",
            element: (row) => {
                return (
                    <div className="flex items-center">
                        <img
                            src={
                                row.productImage ||
                                require("../../assets/images/neural.png")
                                    .default
                            }
                            className="table-user-img"
                            alt="UserImage"
                        />
                        <span className="user-name-area text-left">
                            {row.productName}
                        </span>
                    </div>
                );
            },
        },
        {
            name: "materialNames",
            element: (row) => {
                return (
                    <>
                        <ProductTags
                            tags={getMaterialNames(row)}
                            limit={row.isBundle ? 5 : null}
                            center={true}
                        />
                    </>
                );
            },
        },
        {
            name: "parameters",
            element: (row) => {
                return (
                    <div className="pl-3">
                        <ProductTags
                            tags={row?.parameters || getSubParameterNames(row)}
                            center={true}
                            listClassName={
                                row.isBundle ? "whitespace-nowrap" : ""
                            }
                        />
                    </div>
                );
            },
        },
        {
            name: "isBundle",
            element: (row) => (
                <div
                    className={`tag-ele tag-${
                        row?.isBundle ? "bundle" : "deployed"
                    }`}
                >
                    {t(`${row?.isBundle ? "bundle" : "model"}`)}
                </div>
            ),
        },
        { name: "developedBy", innerKey: "name" },
        { name: "duration", element: (row) => durationFormatter(row.duration) },
        {
            name: "subscribedAt",
            element: (row) => {
                return longDateTime(row.subscribedAt);
            },
        },
        {
            name: "deployed",
            element: (row) => {
                if (isEmpty(row.bundleProducts))
                    return (
                        <div className="flex items-center justify-center">
                            <div
                                className={`tag-ele tag-${
                                    row?.material?.deployed
                                        ? "deployed"
                                        : "undeployed"
                                } px-2`}
                            >
                                {row?.material?.deployed
                                    ? t("deployed")
                                    : t("notDeployed")}
                            </div>
                        </div>
                    );
                else return getSubDeployedStatus(row);
            },
        },
        {
            name: "isLatest",
            element: (row) =>
                row ? (
                    <div className="flex items-center justify-center">
                        <div
                            className={`tag-ele tag-${
                                row.isLatest ? "completed" : "bundle"
                            } px-2`}
                        >
                            {t(`${row.isLatest ? "latest" : "outdated"}`)}
                        </div>
                    </div>
                ) : (
                    "---"
                ),
        },
        {
            element: (row, index, mainRows, mainIndex) => {
                return (
                    isAuthorized(
                        [ROLES.support, ROLES.partnerAdmin, ROLES.admin],
                        getRoles()
                    ) && (
                        <ActionsDropdown
                            row={row}
                            attributes={subscriptionActions(row, mainIndex)}
                            appendTo=".actions-responsive-table"
                        />
                    )
                );
            },
        },
    ];

    const innerHeaders = [
        { label: t("name"), name: "productName", skipSort: true },
        {
            label: t("materialName"),
            name: "productMaterialName",
            skipSort: true,
        },
        { label: t("parameters"), name: "parameters", skipSort: true },
        { label: t("status"), name: "deployed" },
        {
            label: t("actions"),
            name: "actions",
            skipSort: true,
            roles: !isAuthorized(
                [ROLES.support, ROLES.partnerAdmin, ROLES.admin],
                getRoles()
            ),
        },
    ];

    const innerSubscribedKeys = [
        {
            name: "name",
            element: (row) => {
                return (
                    <div className="flex items-center">
                        <img
                            src={
                                row.productImage ||
                                require("../../assets/images/neural.png")
                                    .default
                            }
                            className="table-user-img"
                            alt="UserImage"
                        />
                        <span className="user-name-area text-left">
                            {row.productName}
                        </span>
                    </div>
                );
            },
        },
        {
            name: "materialNames",
            element: (row) => {
                return (
                    <ProductTags
                        tags={[row?.material?.materialName]}
                        center={true}
                    />
                );
            },
        },
        {
            name: "parameters",
            element: (row) => {
                return (
                    <div className="flex justify-center">
                        <ProductTags tags={row.parameters} />
                    </div>
                );
            },
        },
        {
            name: "deployed",
            element: (row) => {
                return (
                    <div className="flex items-center justify-center">
                        <div
                            className={`tag-ele tag-${
                                row?.material?.deployed
                                    ? "deployed"
                                    : "undeployed"
                            } px-2`}
                        >
                            {row?.material?.deployed
                                ? t("deployed")
                                : t("notDeployed")}
                        </div>
                    </div>
                );
            },
        },
        {
            element: (row, index, mainRows) => {
                return (
                    isAuthorized(
                        [ROLES.support, ROLES.partnerAdmin, ROLES.admin],
                        getRoles()
                    ) && (
                        <ActionsDropdown
                            row={row}
                            attributes={subSubscriptionActions(row, mainRows)}
                            appendTo=".actions-responsive-table"
                        />
                    )
                );
            },
        },
    ];

    const subscriptionsSearchFilters = [
        {
            label: t("productName"),
            name: "productName",
            type: "Input",
            placeholder: "ex. model",
        },
        {
            label: t("materialName"),
            name: "materialName",
            type: "Input",
            placeholder: t("materialNamePlaceholder"),
        },
        {
            label: t("subscribedAt"),
            name: "subscribedAt",
            type: "Date",
            max: getCurrentDateYMD(),
        },
    ];

    const subSubscriptionActions = (row, mainRows) => [
        {
            name: row?.material?.deployed ? t("undeploy") : t("deploy"),
            icon: row?.material?.deployed
                ? "fas fa-arrow-down"
                : "fas fa-arrow-up",
            authorized: isAuthorized(
                [ROLES.partnerAdmin, ROLES.support],
                getRoles()
            ),
            handler: () => deploySubscription({ id: mainRows.id, ...row }),
        },
    ];
    const subscriptionActions = (row, index) => [
        {
            name: t("editAccess"),
            icon: "fas fa-users-cog",
            handler: () => {
                setSelectedProduct(subscriptions[index]);
                setAccessModal(true);
            },
            authorized: isAuthorized(
                [ROLES.support, ROLES.partnerAdmin],
                getRoles()
            ),
        },
        {
            name: row?.material?.deployed ? t("undeploy") : t("deploy"),
            icon: row?.material?.deployed
                ? "fas fa-arrow-down"
                : "fas fa-arrow-up",
            authorized:
                isEmpty(row.bundleProducts) &&
                isAuthorized([ROLES.partnerAdmin, ROLES.support], getRoles()),
            handler: () => deploySubscription(row),
        },
        {
            name: t("upgrade"),
            icon: "fas fa-redo",
            authorized: isAuthorized(
                [ROLES.partnerAdmin, ROLES.support],
                getRoles()
            ),
            handler: () => {
                setSelectedProduct(subscriptions[index]);
                setUpgradeModal(true);
            },
            isDisabled: () => row.isLatest,
        },
        {
            name: t("biasCorrection"),
            icon: "fas fa-cog",
            authorized: isAuthorized(
                [ROLES.partnerAdmin, ROLES.support, ROLES.admin],
                getRoles()
            ),
            handler: () => {
                setSelectedProduct(subscriptions[index]);
                setSubscriptionsBiasModal(true);
            },
        },
        {
            name: t("displayUnit"),
            icon: "fas fa-percentage",
            authorized: isAuthorized(
                [ROLES.partnerAdmin, ROLES.support],
                getRoles()
            ),
            handler: () => {
                setSelectedProduct(subscriptions[index]);
                setDisplayUnitModal(true);
            },
        },
        {
            color: "red",
            lineBreak: true,
            name: t("unsubscribe"),
            icon: "fas fa-ban",
            authorized: isAuthorized(
                [ROLES.partnerAdmin, ROLES.support],
                getRoles()
            ),
            handler: () => unsubscribeHandler(row),
        },
    ];

    useTranslationCallback(() => {
        fetchSubscriptions(subscriptionAppliedFilters);
    }, []);

    useEffect(() => {
        if (subscriptionSortBy && subscriptionSortDirection) {
            setSubscriptionCurrentPage(1);
            fetchSubscriptions(
                subscriptionAppliedFilters,
                subscriptionSortBy,
                subscriptionSortDirection
            );
        }
    }, [subscriptionSortBy, subscriptionSortDirection]);

    useEffect(() => {
        if (upgradeModal === true) getLatestVersion(selectedProduct.productId);
    }, [upgradeModal]);

    useEffect(() => {
        if (selectedProduct && selectedProduct.id) {
            subscriptionMap[selectedProduct.id]
                ? setSelectedProductDetails(subscriptionMap[selectedProduct.id])
                : getSubscriptionsDetails();
        }
    }, [selectedProduct]);

    const getSubscriptionsDetails = async () => {
        setIsModalLoading(true);

        let result = await LookupsAPI.getSubscribedProductsDetails(
            selectedProduct.id
        );

        setSelectedProductDetails(result);
        setSubscriptionMap((prevMap) => ({
            ...prevMap,
            [selectedProduct.id]: result,
        }));

        setIsModalLoading(false);
        return result;
    };

    const fetchSubscriptions = async (
        _filters = null,
        _sortBy = null,
        _sortDirection = null,
        offset = 0,
        itemsPerPage = itemsPerRequest
    ) => {
        setIsSubscriptionsLoading(true);
        const newFilter = getMappedFilters(_filters);
        const _offset = offset < 0 ? 0 : offset;

        let result = await OrganizationSubscriptionsAPI.getSubscriptions(
            itemsPerPage,
            newFilter,
            _offset,
            _sortBy,
            _sortDirection
        );
        setSubscriptions(result?.data || []);
        setSubscriptionTotalCount(result?.totalCount || 0);
        setIsSubscriptionsLoading(false);
    };

    const deploySubscription = async (subscription, force = false) => {
        if (
            subscription?.id &&
            subscription?.material?.subscriptionMaterialId
        ) {
            try {
                const result =
                    await OrganizationSubscriptionsAPI.deploySubscription({
                        id: subscription.id,
                        subscriptionMaterialId:
                            subscription?.material?.subscriptionMaterialId,
                        force: force,
                    });
                if (result?.modified) {
                    SuccessAlert(
                        subscription.material.deployed
                            ? t("unDeploySubscriptionSuccess")
                            : t("deploySubscriptionSuccess")
                    );
                    LogCapture.info(
                        `Subscription ${
                            subscription.material.deployed
                                ? t("undeploy")
                                : t("deploy")
                        } successfully with id: ${subscription.id}`
                    );
                    refetchCurrentPage();
                    setConflictedMaterials([]);
                } else if (result.conflict) {
                    actionRow.current = {
                        id: subscription.id,
                        material: { ...subscription?.material },
                        deployed: subscription.material.deployed,
                    };
                    setForceDeployModal(true);
                }
            } catch (error) {
                // ErrorAlert(t("deploySubscriptionFailure"));
                LogCapture.error(
                    `Couldn't ${
                        subscription.material.deployed
                            ? t("undeploy")
                            : t("deploy")
                    } subscription with id: ${subscription.id}`
                );
            }
        }
    };

    const unsubscribeHandler = (model) => {
        setUnsubscribeModal(true);
        actionRow.current = { id: model.id, name: model.productName };
    };

    const unsubscribeModel = async () => {
        setIsModalSubmitting(true);
        const subscriptionId = actionRow.current.id;
        if (subscriptionId) {
            const result = await OrganizationSubscriptionsAPI.unsubscribeModel(
                subscriptionId
            );
            if (result.modified) {
                SuccessAlert(t("unsubscriptionSuccess"));
                LogCapture.info(
                    `Unsubscribed successfully from subscription with id: ${subscriptionId}`
                );
                let newCurrentPage = subscriptionCurrentPage;
                if (subscriptions.length <= 1 && subscriptionCurrentPage > 1) {
                    newCurrentPage = subscriptionCurrentPage - 1;
                }

                setSubscriptionCurrentPage(newCurrentPage);
                refetchCurrentPage(newCurrentPage);
            } else {
                LogCapture.error(
                    `Couldn't unsubscribe from id: ${subscriptionId}`
                );
            }
        }
        setIsModalSubmitting(false);
    };

    const getLatestVersion = async (productId) => {
        const result = await ProductVersionAPI.getActiveVersion(productId);
        setSelectedLatestVersionData(result);
    };

    const upgradeToLatest = async () => {
        setUpgradeModalSubmitting(true);
        const result = await OrganizationSubscriptionsAPI.upgradeVersion({
            organizationSubscriptionId: selectedProduct.id,
            newerVersionId: selectedLatestVersionData.id,
            upgradeBiasCorrections: isCheckboxSelected,
            isBundle: selectedProduct.isBundle,
        });

        if (result?.modified) {
            if (result?.errors?.forceDeploy) {
                const materialsWithConflict = result?.errors?.forceDeploy;
                actionRow.current = {
                    id: selectedProduct.id,
                    deployed: selectedProduct.material.deployed,
                };
                setConflictedMaterials(materialsWithConflict);
                setForceDeployModal(true);
            } else {
                refetchCurrentPage();
            }
            setUpgradeModal(false);
            SuccessAlert(t("updateProductVersionSuccessMessage"));
            setSelectedProduct(null);
            setSelectedLatestVersionData(null);
        } else {
            ErrorAlert(t("updateProductVersionFailMessage"));
        }
        setUpgradeModalSubmitting(false);
    };

    const applySubscriptionFilters = (applyFilters) => {
        fetchSubscriptions(
            applyFilters,
            subscriptionSortBy,
            subscriptionSortDirection
        );
        setSubscriptionFiltersUsed(true);
    };

    const handleSubscriptionPageClick = (index) => {
        if (index !== subscriptionCurrentPage) {
            setSubscriptionCurrentPage(index);
            fetchSubscriptions(
                subscriptionFilters,
                subscriptionSortBy,
                subscriptionSortDirection,
                index - 1
            );
        }
    };

    const refetchCurrentPage = (newCurrentPage) => {
        fetchSubscriptions(
            subscriptionAppliedFilters,
            subscriptionSortBy,
            subscriptionSortDirection,
            (newCurrentPage || subscriptionCurrentPage) - 1
        );
    };

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setSubscriptionCurrentPage(1);
        setObject("Subscribed-Products-itemsPerPage", itemsPerPage, true);
        // call API to update the items per page
        fetchSubscriptions(
            subscriptionFilters,
            subscriptionSortBy,
            subscriptionSortDirection,
            0,
            itemsPerPage
        );
    };

    const isLoading = () => {
        if (isSubscriptionsLoading) return true;
        return false;
    };

    const renderUpgradeModalContent = () => {
        return (
            <>
                <div className="message-modal-wrapper delete-box ">
                    <p>{t("versionUpgradeMessage")}</p>
                    <ChangeCard
                        from={selectedProduct?.productVersion}
                        to={selectedLatestVersionData?.version}
                    />
                </div>

                {selectedLatestVersionData?.whatsNew && (
                    <div className="w-full py-3">
                        <div className="prod-title less-weight product-info-what-new mt-4">
                            <h3 className="font-medium">{t("whatsNew")}</h3>
                            <ul className="small-scroll-y overflow-x-hidden w-full max-h-18 overflow-auto break-words">
                                {renderBulletPoints(
                                    selectedLatestVersionData?.whatsNew
                                )}
                            </ul>
                        </div>
                    </div>
                )}

                <div className="w-full py-2 checkbox-wrapper-input filter-checker-btn">
                    <input
                        type="checkbox"
                        className="checkbox-element"
                        onChange={() =>
                            setIsCheckboxSelected((prevState) => !prevState)
                        }
                        id="biasCorrection"
                        checked={isCheckboxSelected}
                    />
                    <label htmlFor="biasCorrection">
                        <p>
                            <Trans i18nKey="biasMigrationMessage">
                                Do you wish to migrate the Bias Correction of
                                the last version to the new one?
                            </Trans>
                        </p>
                    </label>
                </div>
            </>
        );
    };

    const forceModalSubmitAction = () => {
        deploySubscription(actionRow.current, true);
        setForceDeployModal(false);
    };

    return (
        <>
            <div id="model-content" className="model-content">
                <header className="main-content-header flex items-center justify-between">
                    <h1 className="h1">
                        <Trans i18nKey="subscribedProducts">
                            Subscribed Products
                        </Trans>
                    </h1>
                    <div className="action-header-wrapper flex items-center">
                        <FilterDropdown
                            filterOptions={subscriptionsSearchFilters}
                            filters={subscriptionFilters}
                            setFilters={setSubscriptionFilters}
                            applyFilters={applySubscriptionFilters}
                            component={"subscription-model"}
                            appliedFilters={subscriptionAppliedFilters}
                            setAppliedFilters={setSubscriptionAppliedFilters}
                            disabled={isLoading()}
                        />
                    </div>
                </header>
            </div>
            <NestedTable
                headers={subscribedHeaders}
                keys={subscribedKeys}
                rows={subscriptions}
                sortBy={subscriptionSortBy}
                sortDirection={subscriptionSortDirection}
                setSortBy={setSubscriptionSortBy}
                setSortDirection={setSubscriptionSortDirection}
                subHeaders={innerHeaders}
                subKeys={innerSubscribedKeys}
                subRowsKey={"bundleProducts"}
                isLoading={isLoading()}
                isFilterUsed={subscriptionFiltersUsed}
                entity={"subscribedProducts"}
                includeText={!isLoading()}
                currentPage={subscriptionCurrentPage}
                onPageChange={handleSubscriptionPageClick}
                totalCount={subscriptionTotalCount}
                sizePerPage={itemsPerRequest}
                handleItemsPerPage={handleItemsPerPage}
                missingSubRowMessage="---"
                tableClassName="actions-responsive-table action-wrapper"
            />

            <BaseAlertModal
                isVisible={forceDeployModal}
                setIsVisible={setForceDeployModal}
                title={t("forceDeployModalTitle")}
                message={
                    !isEmpty(conflictedMaterials)
                        ? t("forceDeployModalMessageWithNames", {
                              materials: conflictedMaterials.join(", "),
                          })
                        : t("forceDeployModalMessage")
                }
                action={{
                    submitAction: forceModalSubmitAction,
                    cancelCallback: async () => {
                        refetchCurrentPage();
                    },
                }}
                type="warning"
            />

            <BaseAlertModal
                isVisible={unsubscribeModal}
                setIsVisible={setUnsubscribeModal}
                title={`${t("unsubscribeModalTitle")} ${
                    actionRow.current.name
                }?`}
                message={t("unsubscribeModalMessage")}
                isSubmitting={isModalSubmitting}
                action={{
                    submitAction: unsubscribeModel,
                }}
                type="warning"
            />
            <BaseModal
                isVisible={upgradeModal}
                setIsVisible={setUpgradeModal}
                title={
                    <>
                        <i className="fas fa-check-square" />
                        {t("upgradeProduct")}
                    </>
                }
                message={
                    <>
                        {`${t("upgradeProductConfirmationMessage")} `}
                        <strong>{selectedProduct?.productName}</strong>
                        {" ?"}
                    </>
                }
                action={{
                    submitAction: upgradeToLatest,
                }}
                type="confirmation"
                isSmall={true}
                isSubmitting={upgradeModalSubmitting}
            >
                {renderUpgradeModalContent()}
            </BaseModal>
            <SubscribedBiasCorrection
                isVisible={subscriptionsBiasModal}
                setIsVisible={setSubscriptionsBiasModal}
                selectedProduct={selectedProduct}
                isLoading={isModalLoading}
                selectedProductDetails={selectedProductDetails}
                getSubscriptionsDetails={getSubscriptionsDetails}
            />
            <DisplayUnitModal
                isVisible={displayUnitModal}
                setIsVisible={setDisplayUnitModal}
                selectedProduct={selectedProduct}
                isLoading={isModalLoading}
                selectedProductDetails={selectedProductDetails}
            />
            {isAuthorized([ROLES.support, ROLES.partnerAdmin], getRoles()) && (
                <ProductAccessModal
                    isVisible={accessModal}
                    setIsVisible={setAccessModal}
                    selectedProduct={selectedProduct}
                />
            )}
        </>
    );
};

export default SubscribedProduct;
